import { useMemo } from 'react';
import { noop } from 'lodash';
import type { MutationResult } from '@apollo/client';
import type { StripeAddressElementChangeEvent } from '@stripe/stripe-js';
import type {
  Account,
  GetPaymentMethodQuery,
  UpdatePaymentCardMutation,
} from '@frameio/graphql-client/generated';
import {
  GetPaymentMethod,
  useUpdatePaymentCardMutation,
} from '@frameio/graphql-client/generated';
import { useActiveAccountId } from '@frameio/shared/contexts';
import {
  useAccountRoleTracking,
  useCurrentPlanTracking,
} from '@frameio/shared/plans/hooks';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import type { SubscriptionUpdateSource } from '../../feature-gate/config';

type AccountQueryResult = {
  account: Account | null;
};

export type TrackingBillingFieldsUpdated =
  | ['PaymentMethod']
  | ['BillingAddress']
  | ['PaymentMethod', 'BillingAddress'];

export const trackingBillingFields: {
  [key: string]: TrackingBillingFieldsUpdated;
} = {
  billingAddress: ['BillingAddress'],
  paymentMethod: ['PaymentMethod'],
  paymentMethodAndBillingAddress: ['PaymentMethod', 'BillingAddress'],
};

type UpdatePaymentCardParams = {
  addressElementValue?: StripeAddressElementChangeEvent['value'];
  // if we don't provide the stripeTokenId, we will only update the billing address
  stripeTokenId?: string;
  trackingBillingFieldsUpdated: TrackingBillingFieldsUpdated;
  subscriptionUpdateSource?: SubscriptionUpdateSource;
};

type UpdatePaymentCard = [
  updatePaymentCard: (params: UpdatePaymentCardParams) => void,
  options: MutationResult<UpdatePaymentCardMutation>,
];

/**
 * Calls the updatePaymentCard mutation and updates the account cache with the new payment information
 * @param onCompleted - callback function to be called when the mutation is completed
 * @returns - a tuple containing the updatePaymentCard function and the options object
 */
export default function useUpdatePaymentCard(
  onCompleted = noop
): UpdatePaymentCard {
  const [updatePaymentCard, options] = useUpdatePaymentCardMutation({
    errorPolicy: 'all',
    onCompleted,
  });

  const accountId = useActiveAccountId() ?? '';
  const currentPlanTracking = useCurrentPlanTracking();
  const accountRoleTracking = useAccountRoleTracking();

  const track = useTrackUnsafe();

  return useMemo(
    () => [
      ({
        addressElementValue,
        stripeTokenId,
        subscriptionUpdateSource,
        trackingBillingFieldsUpdated,
      }) => {
        if (!addressElementValue) {
          return;
        }

        track('billing-info-updated', {
          ...currentPlanTracking,
          ...accountRoleTracking,
          billing_fields_updated: trackingBillingFieldsUpdated,
          subscription_update_source: subscriptionUpdateSource,
        });

        updatePaymentCard({
          variables: {
            input: {
              accountId,
              city: addressElementValue.address.city,
              country: addressElementValue.address.country,
              line1: addressElementValue.address.line1,
              line2: addressElementValue.address.line2,
              nameOnCard: addressElementValue.name,
              postalCode: addressElementValue.address.postal_code,
              state: addressElementValue.address.state,
              source: stripeTokenId,
            },
          },
          refetchQueries: ['GetPlanQuote'],
          update(cache, { data }) {
            if (!data?.updatePaymentCard) {
              // when there is an error in the results, updatePaymentCard will be null
              return;
            }

            // paymentMethod does not have an ID to normalize the data, so we have
            // to update the data in the cache
            const existingAccount = cache.readQuery({
              query: GetPaymentMethod,
              variables: {
                accountId,
              },
            }) as AccountQueryResult | null;

            if (!existingAccount?.account) {
              return;
            }

            // update the account cache with the new payment information
            const updatedAccount: AccountQueryResult = {
              ...existingAccount,
              account: {
                ...existingAccount.account,
                subscription: {
                  ...existingAccount.account.subscription,
                  paymentMethod: data.updatePaymentCard.paymentMethod,
                },
              },
            };

            cache.writeQuery({
              query: GetPaymentMethod,
              data: updatedAccount as GetPaymentMethodQuery,
              variables: {
                accountId,
              },
            });
          },
        });
      },
      options,
    ],
    [
      accountId,
      currentPlanTracking,
      accountRoleTracking,
      options,
      track,
      updatePaymentCard,
    ]
  );
}
